import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Edit, Plus, Trash2, Loader, Download, X } from 'lucide-react';
import useSWR from 'swr';

import NewMemberAccountService from '../../services/member-account-service';
import NewRoleService from '../../services/role-service';
import { useAuth } from '../../contexts/AuthContext';

import { STATUS_MESSAGES } from '../../utils/api-handlers/member-account-form';
import { showToast } from '../../components/ToastConfig';

// Fetch roles data
const rolesFetcher = async () => {
  try {
    const res = await NewRoleService.searchMember();
    return [{ name: '', displayName: 'All' }, ...res.data?.data];
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [{ name: '', displayName: 'All' }];
  }
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Unverified', label: 'Unverified' },
  { value: 'Inactive', label: 'Inactive' },
];

const SUBSCRIPTION_LEVEL_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Free', label: 'Free' },
  { value: 'Pro', label: 'Pro' },
  { value: 'Enterprise', label: 'Enterprise' },
];

// Export sections
const EXPORT_SECTIONS = [
  { value: 'Personal Info', label: 'Personal Information' },
  { value: 'Persona', label: 'Persona' },
  { value: 'Role & Subscription', label: 'Role & Subscription' },
  // { value: 'accountManagement', label: 'Account Management' },
];

// SWR fetcher function
const fetcher = async ([url, keyword, role, status, subscriptionLevel, startAt, endAt, page]) => {
  try {
    // Transform URL parameters to API parameters
    const apiParams = {
      search: keyword,
      role,
      // isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
      accountStatus: status,
      subscriptionLevel: subscriptionLevel || "",
      startAt,
      endAt
    };
    const res = await NewMemberAccountService.search(apiParams, page);
    return res.data;
  } catch (error) {
    if (error.response?.status === 400 && ["1002", "8000"].includes(error.response?.data?.errorCode)) {
      return { data: [], pagination: { totalCount: 0 } };
    }
    throw error;
  }
};

// Export Modal Component
function ExportModal({ isOpen, onClose, onExport }) {
  const [selectedSections, setSelectedSections] = useState([]);

  const handleSectionToggle = (section) => {
    if (selectedSections.includes(section)) {
      setSelectedSections(selectedSections.filter(s => s !== section));
    } else {
      setSelectedSections([...selectedSections, section]);
    }
  };

  const handleExport = () => {
    onExport(selectedSections);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[30rem] relative flex justify-center items-center">
        <button
          onClick={onClose}
          className="absolute top-6 right-10 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>
        <div className='-translate-x-4'>
          <h2 className="text-xl font-bold mb-2">Download Member Report</h2>
          <p className="text-gray-600 mb-6">Please select the section you want to download.</p>
          
          <div className="space-y-3 mb-6">
            {EXPORT_SECTIONS.map((section) => (
              <label key={section.value} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedSections.includes(section.value)}
                  onChange={() => handleSectionToggle(section.value)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">{section.label}</span>
              </label>
            ))}
          </div>
          
          <div className="flex gap-3 justify-center mt-16 translate-x-4">
            <button
              onClick={onClose}
              className="w-28 py-2 border border-black rounded-lg text-black"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              className="w-28 py-2 bg-gray-800 text-white rounded-lg"
            >
              Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberAccountList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');
  const [pageInput, setPageInput] = useState(searchParams.get('page') || '1');
  const [isComposing, setIsComposing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const Navigate = useNavigate();
  const { logout } = useAuth();

  // Get search parameters from URL
  const keyword = searchParams.get('keyword') || '';
  const role = searchParams.get('role') || '';
  const status = searchParams.get('status') || '';
  const subscriptionLevel = searchParams.get('subscriptionLevel') || '';
  const startAt = searchParams.get('startAt') || '';
  const endAt = searchParams.get('endAt') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = 10;

  // Fetch roles data
  const { data: rolesData } = useSWR('/api/Role/SearchMember', rolesFetcher, {
    revalidateOnFocus: false
  });
  const roleOptions = rolesData || [{ name: '', displayName: 'All' }];

  // Use SWR to fetch data
  const { data, error, isLoading, mutate } = useSWR(
    ['/api/MemberAccountManagement/Search', keyword, role, status, subscriptionLevel, startAt, endAt, page],
    fetcher,
    {
      onError: (err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          logout();
        } else if (err.response?.status !== 400) {
          const errorMessage = STATUS_MESSAGES[err.response?.status] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        } else {
          const errorMessage = STATUS_MESSAGES[err.response?.data?.errorCode] || STATUS_MESSAGES["default"];
          showToast.error(errorMessage);
        }
      },
      revalidateOnFocus: false,
    }
  );

  const [accountsWithStatus, setAccountsWithStatus] = useState([]);
  const accounts = data?.data || [];
  const totalItems = data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {
    if (accounts.length > 0) {
      setAccountsWithStatus(accounts.map(account => ({
        ...account,
        isUpdating: false
      })));
    }
  }, [accounts]);

  // const handleToggleStatus = async (e, accountId, newStatus) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   // Update local state to show loading
  //   setAccountsWithStatus(prev => prev.map(account => 
  //     account.id === accountId 
  //       ? { ...account, isUpdating: true }
  //       : account
  //   ));

  //   const targetAccount = accountsWithStatus.find(account => account.id === accountId);
    
  //   if (!targetAccount) {
  //     console.error('Target account not found');
  //     showToast.error('Something went wrong. Please try again or refresh the page.');
  //     return;
  //   }

  //   try {
  //     await NewMemberAccountService.update(accountId, {...targetAccount, isEnabled: newStatus});
  //     mutate(); // Refresh the data
  //     showToast.success(newStatus ? 'Account has been activated' : 'Account has been deactivated');
  //   } catch (error) {
  //     console.error('Error updating account status:', error);
  //     if (error?.response?.status === 401 || error?.response?.status === 403) {
  //       logout();
  //     } else if (error.response?.status !== 400) {
  //       const errorMessage = STATUS_MESSAGES[error.response?.status] || STATUS_MESSAGES["default"];
  //       showToast.error(errorMessage);
  //     } else {
  //       const errorMessage = STATUS_MESSAGES[error.response?.data?.errorCode] || STATUS_MESSAGES["default"];
  //       showToast.error(errorMessage);
  //     }

  //     // Revert local state on error
  //     setAccountsWithStatus(prev => prev.map(account => 
  //       account.id === accountId 
  //         ? { ...account, isUpdating: false }
  //         : account
  //     ));
  //   }
  // };

  // Handle search params update
  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
    if (updates.page) {
      setPageInput(updates.page);
    }
  }, [searchParams, setSearchParams]);

  // Search debounce
  useEffect(() => {
    if (isComposing) return;
    if (searchInput === keyword) return;

    const timer = setTimeout(() => {
      updateSearchParams({ keyword: searchInput, page: '1' });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, isComposing, updateSearchParams, keyword]);

  // Export handler
  const handleExport = async (selectedSections) => {
    try {
      // Prepare export parameters with current filters
      const exportParams = {
        search: keyword,
        role,
        // isEnabled: status === 'inactive' ? false : status === 'active' ? true : null,
        accountStatus: status,
        subscriptionLevel: subscriptionLevel || "",
        startAt,
        endAt,
        sections: selectedSections
      };

      const res = await NewMemberAccountService.exportToExcel(exportParams);
      window.open(`${process.env.REACT_APP_FILE_BASIC}${res.data}`, "_blank");
      
      showToast.success('Member report exported successfully.');
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting data:', error);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      } else {
        showToast.error('Export failed, please try again.');
      }
    }
  };

  return (
    <div className={`p-6 pt-0 ${totalPages > 1 ? "h-[78vh]" : ""} overflow-auto`}>
      <div className="container mx-auto">
        <div className="sticky top-0 pt-6 pb-4 bg-white z-10">
          <div className='flex justify-between items-center'>
            <h1 className="text-2xl font-bold">Manage Members</h1>
            <div className='flex items-center space-x-4'>
              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-2 px-4 py-2"
              >
                <Download size={20} />
                <span>Export</span>
              </button>
              <div className='relative'>
                <input
                  type="date"
                  value={startAt}
                  onChange={(e) => updateSearchParams({ startAt: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                  placeholder="Start Date"
                />
                <span className='bg-white p-1 block absolute top-0 left-3 -translate-y-1/2 text-sm text-gray-600'>From Date</span>
              </div>
              <div className='relative'>
                <input
                  type="date"
                  value={endAt}
                  onChange={(e) => updateSearchParams({ endAt: e.target.value, page: '1' })}
                  className="px-3 py-2 border rounded"
                  placeholder="End Date"
                />
                <span className='bg-white p-1 block absolute top-0 left-3 -translate-y-1/2 text-sm text-gray-600'>To Date</span>
              </div>
            </div>
          </div>
          
          {/* Search and filter controls */}
          <div className="space-y-4">
            <div className="flex justify-between mt-4">
              <div className="flex items-center gap-4">
                <Link className='flex items-center gap-2' to="/member/account/new">
                    <Plus size={24} className='border border-gray-600 rounded-full p-1' />
                    <span>Create</span>
                </Link>
              </div>
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="Search Account..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={() => setIsComposing(false)}
                  className="px-3 py-2 border rounded"
                />
                <select
                  value={role || 'placeholder-role'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-role' ? '' : e.target.value;
                    updateSearchParams({ role: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-role" disabled hidden>User Role</option>
                  {roleOptions.map((option, index) => (
                    <option key={option.name || index} value={option.name}>{option.displayName}</option>
                  ))}
                </select>
                <select
                  value={status || 'placeholder-status'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-status' ? '' : e.target.value;
                    updateSearchParams({ status: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-status" disabled hidden>Status</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={subscriptionLevel || 'placeholder-plan'}
                  onChange={(e) => {
                    const value = e.target.value === 'placeholder-plan' ? '' : e.target.value;
                    updateSearchParams({ subscriptionLevel: value, page: '1' });
                  }}
                  className="px-3 py-2 border rounded"
                >
                  <option value="placeholder-plan" disabled hidden>Current Plan</option>
                  {SUBSCRIPTION_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="text-center py-8 text-gray-500 flex items-center justify-center">
            <Loader className="mr-1 animate-spin w-5 h-5" />
            Loading...
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="text-center py-8 text-red-500">
            Error when loading data. Please try again or refresh the page.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && accounts.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {keyword || role || status || subscriptionLevel || startAt || endAt ? 'No users matching the criteria' : 'No users existing currently'}
          </div>
        )}

        {/* Account list */}
        {!isLoading && !error && accounts.length > 0 && (
          <div className="space-y-2">
            {/* Table header */}
            <div className="flex items-center p-4 bg-gray-50 rounded-t border-b font-medium justify-between">
              <div className="flex-1 flex">
                <div className="w-[5%]">ID</div>
                <div className="w-[10%]">Create Time</div>
                <div className="w-[15%]">User Name</div>
                <div className="w-[30%]">Email</div>
                <div className="w-[20%]">User Role</div>
                <div className="w-[10%]">Current Plan</div>
                <div className="w-[10%] text-center">Status</div>
              </div>
            </div>

            {/* Table body */}
            {accounts.map(account => (
              <Link key={account.id} to={`/member/account/${account.id}`} className="flex items-center p-4 border rounded hover:bg-gray-50 justify-between">
                <div className="flex-1 flex">
                  <div className="w-[5%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.id}</div>
                  <div className="w-[10%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.createTime?.substring(0, 10)}</div>
                  <div className="w-[15%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.firstName} {account.lastName}</div>
                  <div className="w-[30%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.email}</div>
                  <div className="w-[20%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.roleName}</div>
                  <div className="w-[10%] wrap-anywhere pr-4" style={{overflowWrap: "break-word"}}>{account.subscriptionLevel}</div>
                  <div className="w-[10%] wrap-anywhere" style={{overflowWrap: "break-word"}}>
                    {/* <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggleStatus(e, account.id, account.isEnabled !== true);
                      }}
                      disabled={account.isUpdating}
                      className={`relative inline-flex h-7 w-12 items-center rounded-full
                        ${account.isEnabled === true ? 'bg-activationBtnGreen' : 'bg-activationBtnGray'}
                        ${account.isUpdating ? 'cursor-not-allowed' : ''}
                      `}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full transition-transform duration-300 bg-white ${
                          account.isEnabled === true ? 'translate-x-[1.35rem]' : 'translate-x-[0.1rem]'
                        }`}
                      />
                    </button> */}
                    {account.accountStatus}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        
        {!isLoading && !error && (<p className={`${totalPages > 1 ? "fixed bottom-[4.5%]" : "mt-4"} px-4 font-semibold`}>{totalItems.toString()} Members</p>)}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-4 fixed bottom-[4%] w-[calc(100%-21rem)] max-w-[1200px]">
            <div className="flex items-center">
              <span className="text-gray-700">Page</span>
              <input
                type="text"
                className="w-16 mx-2 px-2 py-1 border rounded text-center"
                value={pageInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setPageInput(value);
                }}
                onBlur={() => {
                  const pageNum = parseInt(pageInput);
                  if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
                    setPageInput(page.toString());
                  }
                }}
              />
            </div>
            
            {/* Pagination buttons */}
            <div className="flex flex-wrap justify-center gap-2 w-full md:w-auto px-4">
              <button
                onClick={() => updateSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className={`px-3 py-1 border rounded ${
                  page === 1 ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Prev
              </button>
              
              {/* First page */}
              <button
                onClick={() => updateSearchParams({ page: '1' })}
                className={`px-3 py-1 border rounded ${
                  1 === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                }`}
              >
                1
              </button>

              {/* Pagination dots and numbers */}
              {page > 6 && <span className="px-3 py-1">...</span>}

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(pageNum => {
                  const showRange = 4;
                  return pageNum > page - showRange && pageNum < page + showRange;
                })
                .filter(pageNum => pageNum !== 1 && pageNum !== totalPages)
                .map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    className={`px-3 py-1 border rounded ${
                      pageNum === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

              {page < totalPages - 5 && <span className="px-3 py-1">...</span>}

              {/* Last page */}
              {totalPages > 1 && (
                <button
                  onClick={() => updateSearchParams({ page: String(totalPages) })}
                  className={`px-3 py-1 border rounded ${
                    totalPages === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                  }`}
                >
                  {totalPages}
                </button>
              )}

              <button
                onClick={() => updateSearchParams({ page: String(page + 1) })}
                disabled={page === totalPages}
                className={`px-3 py-1 border rounded ${
                  page === totalPages ? 'text-gray-400' : 'hover:bg-gray-100'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
      />
    </div>
  );
}

export default MemberAccountList;